import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Lock, CheckCircle2, AlertCircle } from 'lucide-react';

interface EmpresaData {
  id: string;
  nome: string;
  email: string;
  is_premium: boolean;
}

interface ActivationLog {
  id: string;
  empresa_id: string;
  empresa_nome: string;
  empresa_email: string;
  plano: string;
  tem_nota_fiscal: boolean;
  data_pagamento: string;
  forma_pagamento: string;
  ativado_por: string;
  ativado_em: string;
}

const PLANOS = [
  { value: 'monthly-basic', label: 'Básico Mensal (R$ 39)' },
  { value: 'quarterly-basic', label: 'Básico Trimestral (R$ 79,90)' },
  { value: 'yearly-basic', label: 'Básico Anual (R$ 390)' },
  { value: 'monthly-professional', label: 'Profissional Mensal (R$ 99,90)' },
  { value: 'monthly-professional-plus', label: 'Profissional Plus (R$ 189,90)' },
  { value: 'monthly-professional-enterprise', label: 'Enterprise (R$ 389,90)' },
  { value: 'quarterly-professional', label: 'Profissional Trimestral (R$ 280)' },
  { value: 'yearly-professional', label: 'Profissional Anual (R$ 999,90)' },
];

const FORMAS_PAGAMENTO = [
  { value: 'PIX', label: 'PIX' },
  { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'DEBIT_CARD', label: 'Cartão de Débito' },
];

export default function AdminPanel() {
  const { user, empresa } = useAuth();
  const navigate = useNavigate();

  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [empresaEncontrada, setEmpresaEncontrada] = useState<EmpresaData | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [logs, setLogs] = useState<ActivationLog[]>([]);

  const [formData, setFormData] = useState({
    plano: 'monthly-basic',
    temNotaFiscal: false,
    dataPagamento: new Date().toISOString().split('T')[0],
    formaPagamento: 'PIX',
  });

  // ========== VERIFICAR SE É ADMIN ==========
  useEffect(() => {
    if (!user) {
      setIsLoading(true);
      return;
    }

    const isAdmin = user.email === 'brunobgs1888@gmail.com';
    setIsAuthorized(isAdmin);
    setIsLoading(false);

    if (!isAdmin) {
      console.warn('❌ Acesso negado: Usuário não é admin');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // ========== BUSCAR EMPRESA POR EMAIL ==========
  const handleSearchEmpresa = async () => {
    if (!searchEmail) {
      toast.error('Digite um email para buscar');
      return;
    }

    setIsSearching(true);
    try {
      console.log('🔍 Buscando empresa por email:', searchEmail);

      // Buscar empresa direto pela tabela empresas
      const { data: empresas, error: empresaError } = await supabase
        .from('empresas')
        .select('id, nome, email, is_premium')
        .ilike('email', searchEmail)
        .single();

      if (empresaError) {
        console.error('❌ Erro ao buscar empresa:', empresaError);
        toast.error('Empresa não encontrada');
        setEmpresaEncontrada(null);
        return;
      }

      if (!empresas) {
        toast.error('Empresa não encontrada');
        setEmpresaEncontrada(null);
        return;
      }

      setEmpresaEncontrada(empresas);
      console.log('✅ Empresa encontrada:', empresas);
      toast.success(`✅ Empresa encontrada: ${empresas.nome}`);
    } catch (error) {
      console.error('❌ Erro ao buscar:', error);
      toast.error('Erro ao buscar empresa');
    } finally {
      setIsSearching(false);
    }
  };

  // ========== ATIVAR ACESSO MANUAL ==========
  const handleActivate = async () => {
    if (!empresaEncontrada) {
      toast.error('Selecione uma empresa');
      return;
    }

    setIsActivating(true);
    try {
      const expirationDate = new Date();
      const planoDays = {
        'monthly-basic': 30,
        'quarterly-basic': 91,
        'yearly-basic': 365,
        'monthly-professional': 30,
        'monthly-professional-plus': 30,
        'monthly-professional-enterprise': 30,
        'quarterly-professional': 91,
        'yearly-professional': 365,
      };

      const days = planoDays[formData.plano as keyof typeof planoDays] || 30;
      expirationDate.setDate(expirationDate.getDate() + days);

      console.log('📝 Ativando premium manualmente...');
      console.log('   Empresa:', empresaEncontrada.nome);
      console.log('   Plano:', formData.plano);
      console.log('   Expiração:', expirationDate.toISOString());

      // Atualizar empresa
      const { error: updateError } = await supabase
        .from('empresas')
        .update({
          is_premium: true,
          status: 'active',
          trial_end_date: expirationDate.toISOString(),
          plan_type: formData.plano,
          tem_nota_fiscal: formData.temNotaFiscal,
          updated_at: new Date().toISOString(),
        })
        .eq('id', empresaEncontrada.id);

      if (updateError) {
        throw updateError;
      }

      // Sincronizar em asaas_subscriptions (para getPlanInfo funcionar automaticamente)
      try {
        // Gerar um ID único para uso no admin (já que não vem do ASAAS)
        const adminSubId = `admin-${empresaEncontrada.id}-${Date.now()}`;

        await supabase.from('asaas_subscriptions').upsert({
          empresa_id: empresaEncontrada.id,
          asaas_subscription_id: adminSubId,
          plan_type: formData.plano,
          status: 'ACTIVE',
          billing_type: formData.formaPagamento || 'PIX',
          auto_pix: false,
          updated_at: new Date().toISOString()
        }, { onConflict: 'asaas_subscription_id' });
        console.log('✅ Assinatura sincronizada em asaas_subscriptions');
      } catch (subErr) {
        console.warn('⚠️ Erro ao sincronizar assinatura (não bloqueia):', subErr);
      }

      // Registrar no histórico (não bloqueia se falhar)
      try {
        await supabase.from('admin_activation_logs').insert({
          empresa_id: empresaEncontrada.id,
          empresa_nome: empresaEncontrada.nome,
          empresa_email: empresaEncontrada.email,
          plano: formData.plano,
          tem_nota_fiscal: formData.temNotaFiscal,
          data_pagamento: formData.dataPagamento,
          forma_pagamento: formData.formaPagamento,
          ativado_por: user?.email || 'admin',
          ativado_em: new Date().toISOString(),
        });
        console.log('✅ Log registrado com sucesso');
      } catch (logErr) {
        console.warn('⚠️ Erro ao registrar log (não bloqueia):', logErr);
      }

      toast.success(`✅ Premium ativado para ${empresaEncontrada.nome} até ${expirationDate.toLocaleDateString('pt-BR')}`);
      console.log(`✅ Premium ativado com sucesso para: ${empresaEncontrada.nome}`);

      // Limpar formulário
      setIsDialogOpen(false);
      setEmpresaEncontrada(null);
      setSearchEmail('');
      setFormData({
        plano: 'monthly-basic',
        temNotaFiscal: false,
        dataPagamento: new Date().toISOString().split('T')[0],
        formaPagamento: 'PIX',
      });

      // Recarregar histórico
      await loadLogs();
    } catch (error) {
      console.error('❌ Erro ao ativar:', error);
      toast.error('Erro ao ativar acesso');
    } finally {
      setIsActivating(false);
    }
  };

  // ========== CARREGAR HISTÓRICO ==========
  const loadLogs = async () => {
    try {
      const { data } = await supabase
        .from('admin_activation_logs')
        .select('*')
        .order('ativado_em', { ascending: false })
        .limit(10);

      if (data) {
        setLogs(data);
      }
    } catch (error) {
      console.warn('⚠️ Erro ao carregar logs:', error);
    }
  };

  useEffect(() => {
    if (isAuthorized) {
      loadLogs();
    }
  }, [isAuthorized]);

  // ========== NÃO AUTORIZADO ==========
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <Lock className="w-12 h-12 text-red-600" />
        <h1 className="text-2xl font-bold text-red-600">Acesso Negado</h1>
        <p className="text-gray-600">Apenas administradores podem acessar esta página</p>
      </div>
    );
  }

  // ========== PAINEL ADMIN ==========
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
            <Lock className="w-8 h-8 text-purple-600" />
            Painel de Admin
          </h1>
          <p className="text-gray-600 mt-2">Ativação manual de premium (emergências)</p>
        </div>

        <div className="grid gap-6">
          {/* ========== FORMULÁRIO DE ATIVAÇÃO ========== */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Ativar Acesso Manual
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Buscar Empresa */}
              <div className="space-y-2">
                <Label>Email do Cliente</Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="email@empresa.com"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchEmpresa()}
                  />
                  <Button onClick={handleSearchEmpresa} disabled={isSearching}>
                    {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
                  </Button>
                </div>
              </div>

              {/* Resultado da Busca */}
              {empresaEncontrada && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-semibold text-green-900">{empresaEncontrada.nome}</p>
                  <p className="text-sm text-green-700">
                    Status: {empresaEncontrada.is_premium ? '✅ Premium' : '⭕ Não Premium'}
                  </p>
                  <Button
                    onClick={() => setIsDialogOpen(true)}
                    className="mt-3 bg-green-600 hover:bg-green-700"
                  >
                    Preencher Dados de Ativação
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ========== HISTÓRICO ========== */}
          {logs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Histórico de Ativações Manuais</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left p-2 font-semibold">Empresa</th>
                        <th className="text-left p-2 font-semibold">Plano</th>
                        <th className="text-left p-2 font-semibold">Forma Pagamento</th>
                        <th className="text-left p-2 font-semibold">Ativado em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id} className="border-t hover:bg-gray-50">
                          <td className="p-2">
                            <div>
                              <p className="font-medium">{log.empresa_nome}</p>
                              <p className="text-xs text-gray-500">{log.empresa_email}</p>
                            </div>
                          </td>
                          <td className="p-2">
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                              {log.plano}
                            </span>
                          </td>
                          <td className="p-2">{log.forma_pagamento}</td>
                          <td className="p-2 text-xs text-gray-600">
                            {new Date(log.ativado_em).toLocaleString('pt-BR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ========== DIALOG DE ATIVAÇÃO ========== */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Dados de Ativação</DialogTitle>
            <DialogDescription>
              Preencha os dados de pagamento para {empresaEncontrada?.nome}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Plano */}
            <div className="space-y-2">
              <Label>Plano</Label>
              <Select value={formData.plano} onValueChange={(value) => setFormData({ ...formData, plano: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLANOS.map((plano) => (
                    <SelectItem key={plano.value} value={plano.value}>
                      {plano.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Forma de Pagamento */}
            <div className="space-y-2">
              <Label>Forma de Pagamento</Label>
              <Select value={formData.formaPagamento} onValueChange={(value) => setFormData({ ...formData, formaPagamento: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FORMAS_PAGAMENTO.map((forma) => (
                    <SelectItem key={forma.value} value={forma.value}>
                      {forma.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Data de Pagamento */}
            <div className="space-y-2">
              <Label>Data do Pagamento</Label>
              <Input
                type="date"
                value={formData.dataPagamento}
                onChange={(e) => setFormData({ ...formData, dataPagamento: e.target.value })}
              />
            </div>

            {/* Tem Nota Fiscal */}
            <div className="flex items-center gap-2">
              <Checkbox
                checked={formData.temNotaFiscal}
                onCheckedChange={(checked) => setFormData({ ...formData, temNotaFiscal: checked as boolean })}
              />
              <Label className="cursor-pointer">Tem Nota Fiscal (NF-e)</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleActivate}
              disabled={isActivating}
              className="bg-green-600 hover:bg-green-700"
            >
              {isActivating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Ativando...
                </>
              ) : (
                '✅ Ativar Acesso'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
